// ── Colour wheel ──────────────────────────────────────────────────────────────
// "Any colour at all": hue round the edge, paler towards the centre, plus brightness and a typed
// colour code. Used by New Game's Edit Player and by Manage profile. It applies as you choose —
// there's no separate "use" step that could be missed. A colour too close to one another human
// already has is refused, with a note saying whose.
import { clashWith, type NamedColour } from "./colours.ts";

export interface ColourWheel {
  /** The round wheel swatch to put in a row of colour buttons. */
  button: HTMLButtonElement;
  /** The wheel panel — place it after the row. */
  pop: HTMLDivElement;
  /** Show `c` on the swatch as the chosen custom colour (or clear it with null). */
  mark(c: string | null): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, attrs: Record<string, string> = {}): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = cls;
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  return e;
}

export function colourWheel(opts: { onPick: (c: string) => void; taken?: () => NamedColour[]; swatchClass?: string }): ColourWheel {
  const button = el("button", `${opts.swatchClass ?? "colour-opt"} custom`, { type: "button", title: "Choose any colour", "aria-label": "Choose any colour", "aria-expanded": "false" });
  button.appendChild(el("span", "cw-dot"));
  const pop = el("div", "cw-pop"); pop.hidden = true;
  const S = 200;
  const canvas = el("canvas", "cw-wheel", { width: String(S), height: String(S), "aria-label": "Colour wheel" });
  const bright = el("input", "cw-bright", { type: "range", min: "15", max: "100", value: "90", "aria-label": "Brightness" });
  const preview = el("span", "cw-preview");
  const hex = el("input", "cw-hex", { type: "text", maxlength: "7", spellcheck: "false", "aria-label": "Colour code" });
  const done = el("button", "btn primary cw-use", { type: "button" }); done.textContent = "Done";
  const warn = el("p", "cw-warn", { role: "status" });
  const brightRow = el("label", "cw-row"); const bl = el("span", ""); bl.textContent = "Brightness"; brightRow.append(bl, bright);
  const codeRow = el("div", "cw-row"); codeRow.append(preview, hex, done);
  pop.append(canvas, brightRow, codeRow, warn);

  let hue = 330, sat = 0.75, val = 0.9;
  const hsv = (hh: number, ss: number, vv: number): string => {
    const f = (n: number) => { const k = (n + hh / 60) % 6; return vv - vv * ss * Math.max(0, Math.min(k, 4 - k, 1)); };
    return "#" + [f(5), f(3), f(1)].map((x) => Math.round(x * 255).toString(16).padStart(2, "0")).join("");
  };
  const draw = () => {
    const ctx = canvas.getContext("2d")!, img = ctx.createImageData(S, S), r0 = S / 2;
    for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
      const dx = x - r0 + 0.5, dy = y - r0 + 0.5, d = Math.hypot(dx, dy) / r0, i = (y * S + x) * 4;
      if (d > 1) { img.data[i + 3] = 0; continue; }
      const c = hsv((Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360, d, val);
      img.data[i] = parseInt(c.slice(1, 3), 16); img.data[i + 1] = parseInt(c.slice(3, 5), 16); img.data[i + 2] = parseInt(c.slice(5, 7), 16); img.data[i + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    // marker on the chosen spot
    const a = hue * Math.PI / 180;
    ctx.beginPath(); ctx.arc(r0 + Math.cos(a) * sat * r0, r0 + Math.sin(a) * sat * r0, 7, 0, Math.PI * 2);
    ctx.lineWidth = 3; ctx.strokeStyle = "#fff"; ctx.stroke(); ctx.lineWidth = 1; ctx.strokeStyle = "#000"; ctx.stroke();
  };
  const show = () => { const c = hsv(hue, sat, val); preview.style.background = c; if (document.activeElement !== hex) hex.value = c; draw(); };
  const mark = (c: string | null) => {
    if (c) { button.style.setProperty("--cw", c); button.classList.add("has-colour"); }
    else { button.style.removeProperty("--cw"); button.classList.remove("has-colour"); }
  };
  const apply = () => {
    const c = hsv(hue, sat, val), clash = clashWith(c, opts.taken?.() ?? []);
    warn.textContent = clash ? `Too close to ${clash.name}'s colour — pick something more different.` : "";
    if (clash) return;
    mark(c); opts.onPick(c);
  };
  const at = (e: PointerEvent) => {
    const rc = canvas.getBoundingClientRect(), dx = (e.clientX - rc.left) / rc.width * S - S / 2, dy = (e.clientY - rc.top) / rc.height * S - S / 2;
    hue = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360; sat = Math.min(1, Math.hypot(dx, dy) / (S / 2)); show(); apply();
  };
  canvas.addEventListener("pointerdown", (e) => { canvas.setPointerCapture(e.pointerId); at(e); });
  canvas.addEventListener("pointermove", (e) => { if (canvas.hasPointerCapture(e.pointerId)) at(e); });
  bright.addEventListener("input", () => { val = Number(bright.value) / 100; show(); apply(); });
  hex.addEventListener("input", () => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex.value.trim()); if (!m) return;
    const n = parseInt(m[1], 16), r = (n >> 16) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
    val = mx; sat = mx ? d / mx : 0;
    hue = d === 0 ? 0 : mx === r ? 60 * (((g - b) / d) % 6) : mx === g ? 60 * ((b - r) / d + 2) : 60 * ((r - g) / d + 4);
    hue = (hue + 360) % 360; bright.value = String(Math.round(val * 100)); show(); apply();
  });
  const toggle = (open: boolean) => { pop.hidden = !open; button.setAttribute("aria-expanded", String(open)); if (open) { warn.textContent = ""; show(); } };
  button.addEventListener("click", () => toggle(pop.hidden));
  done.addEventListener("click", () => toggle(false));
  return { button, pop, mark };
}
